import { TrashIcon } from "@heroicons/react/24/outline"
import dayjs from "dayjs"
import React, { useEffect, useRef, useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../components/shared/Buttons"
import DatePicker from "../../components/shared/DatePicker"
import { CheckBox, Label, TimePicker } from "../../components/shared/Inputs"
import { Banner } from "../../components/shared/Layout"
import { useToast } from "../../contexts/ToastContext"

import {
  UPSERT_LOCATION_AVAILABILITY_OVERRIDE,
  buildLocalOverride,
  isPendingOverrideId
} from "./locationAvailabilityOverrides"

const UPSERT_OVERRIDE = UPSERT_LOCATION_AVAILABILITY_OVERRIDE

const DELETE_OVERRIDE = `
  mutation DeleteLocationAvailabilityOverride($id: ID!) {
    deleteLocationAvailabilityOverride(id: $id) {
      result
      errors
    }
  }
`

const defaultBlock = () => ({ startsAt: "10:00 AM", endsAt: "5:00 PM" })

const timeValue = (timeStr) => {
  const parsed = Date.parse(`01/01/2024 ${timeStr}`)
  return Number.isNaN(parsed) ? null : parsed
}

const sameOverride = (left, right) => String(left.id) === String(right.id) || left.overrideDate === right.overrideDate

const LocationSpecialDates = ({ location, onOverridesChange }) => {
  const { showToast } = useToast()
  const [, upsertOverride] = useMutation(UPSERT_OVERRIDE)
  const [, deleteOverride] = useMutation(DELETE_OVERRIDE)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [overrides, setOverrides] = useState(() => location.availabilityOverrides || [])
  const draftFormRef = useRef(null)

  useEffect(() => {
    setOverrides(location.availabilityOverrides || [])
  }, [location.id])

  useEffect(() => {
    if (!draft) return

    requestAnimationFrame(() => {
      draftFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }, [draft])

  const startDraft = (override = null) => {
    if (override) {
      const blocks = JSON.parse(override.blocks || "[]")
      setDraft({
        id: override.id,
        overrideDate: override.overrideDate,
        enabled: override.enabled,
        startsAt: blocks[0]?.starts_at || blocks[0]?.startsAt || "10:00 AM",
        endsAt: blocks[0]?.ends_at || blocks[0]?.endsAt || "5:00 PM"
      })
    } else {
      setDraft({
        overrideDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
        enabled: true,
        ...defaultBlock()
      })
    }
  }

  const saveDraft = async () => {
    if (!draft?.overrideDate) return

    if (draft.enabled) {
      const start = timeValue(draft.startsAt)
      const end = timeValue(draft.endsAt)
      if (start === null || end === null) {
        showToast({ type: "error", content: "Choose valid start and end times" })
        return
      }
      if (end <= start) {
        showToast({ type: "error", content: "End time must be after start time" })
        return
      }
    }

    if (!location?.id) {
      const savedOverride = buildLocalOverride(draft)
      const next = draft.id
        ? overrides.map((item) => (sameOverride(item, savedOverride) ? savedOverride : item))
        : [...overrides, savedOverride].sort((a, b) => a.overrideDate.localeCompare(b.overrideDate))

      setOverrides(next)
      onOverridesChange(next)
      setDraft(null)
      showToast({ type: "success", content: "Special date added — save the location to apply" })
      return
    }

    setSaving(true)
    const blocks = JSON.stringify([{ starts_at: draft.startsAt, ends_at: draft.endsAt }])
    const result = await upsertOverride({
      id: draft.id,
      locationId: location.id,
      overrideDate: draft.overrideDate,
      enabled: draft.enabled,
      blocks
    })
    setSaving(false)

    if (result.data?.upsertLocationAvailabilityOverride?.result === "success") {
      const saved = result.data.upsertLocationAvailabilityOverride.locationAvailabilityOverride
      const savedOverride = { ...saved, blocks }
      const next = draft.id
        ? overrides.map((item) => (sameOverride(item, savedOverride) ? savedOverride : item))
        : [...overrides, savedOverride].sort((a, b) => a.overrideDate.localeCompare(b.overrideDate))

      setOverrides(next)
      onOverridesChange(next)
      setDraft(null)
      showToast({ type: "success", content: "Special date saved" })
    } else {
      const errors = result.data?.upsertLocationAvailabilityOverride?.errors?.join(", ")
      showToast({ type: "error", content: errors || "Could not save special date" })
    }
  }

  const removeOverride = async (override) => {
    if (isPendingOverrideId(override.id) || !location?.id) {
      const next = overrides.filter((item) => !sameOverride(item, override))
      setOverrides(next)
      onOverridesChange(next)
      showToast({ type: "success", content: "Special date removed" })
      return
    }

    const result = await deleteOverride({ id: override.id })
    if (result.data?.deleteLocationAvailabilityOverride?.result === "success") {
      const next = overrides.filter((item) => !sameOverride(item, override))
      setOverrides(next)
      onOverridesChange(next)
      showToast({ type: "success", content: "Special date removed" })
    }
  }

  return (
    <div className="flex flex-col gap-3 border-t border-gray-light pt-6">
      <div>
        <Label>Pop-up / special dates</Label>
        <p className="mt-1 text-sm text-gray-dark">
          Open this location on specific days without changing your weekly schedule — ideal for traveling or one-off
          venues.
        </p>
      </div>

      {!location?.id && (
        <Banner type="info">
          Special dates you add here are saved when you click <strong>Save</strong> at the bottom of this flyout.
        </Banner>
      )}

      {overrides.length === 0 && !draft && (
        <Banner type="info">
          No special dates yet. Add one when you are offering sessions at this location on a day it is normally closed.
        </Banner>
      )}

      {overrides.map((override) => {
        const blocks = JSON.parse(override.blocks || "[]")
        const block = blocks[0] || {}
        const startsAt = block.starts_at || block.startsAt
        const endsAt = block.ends_at || block.endsAt

        return (
          <div
            key={override.id || override.overrideDate}
            className="flex items-center justify-between rounded-lg border border-gray-light p-3 text-sm">
            <div>
              <div className="font-bold">{dayjs(override.overrideDate).format("MMM D, YYYY")}</div>
              <div className="text-gray-dark">{override.enabled ? `${startsAt} – ${endsAt}` : "Closed (override)"}</div>
            </div>
            <div className="flex gap-2">
              <Button type="link" size="small" onClick={() => startDraft(override)}>
                Edit
              </Button>
              <button type="button" aria-label="Delete special date" onClick={() => removeOverride(override)}>
                <TrashIcon className="size-5 text-gray-dark" />
              </button>
            </div>
          </div>
        )
      })}

      {draft ? (
        <div
          ref={draftFormRef}
          className="flex scroll-mt-32 flex-col gap-3 rounded-lg border border-gray-light p-4 md:scroll-mt-28">
          <div>
            <Label>Date</Label>
            <DatePicker
              selected={dayjs(draft.overrideDate).toDate()}
              onChange={(date) => setDraft({ ...draft, overrideDate: dayjs(date).format("YYYY-MM-DD") })}
              minDate={new Date()}
            />
          </div>
          <CheckBox
            label="Open for bookings on this date"
            checked={draft.enabled}
            onChange={() => setDraft({ ...draft, enabled: !draft.enabled })}
          />
          {draft.enabled && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>From</Label>
                <TimePicker
                  valueFormat="h:mm A"
                  value={draft.startsAt}
                  onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
                />
              </div>
              <div>
                <Label>Until</Label>
                <TimePicker
                  valueFormat="h:mm A"
                  value={draft.endsAt}
                  onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="primary" size="small" disabled={saving} onClick={saveDraft}>
              Save date
            </Button>
            <Button type="secondary" size="small" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button type="secondary" size="small" onClick={() => startDraft()}>
          Add special date
        </Button>
      )}
    </div>
  )
}

export default LocationSpecialDates
